/**
 * Empty State - Unified empty and error states for deals page
 */

import { Search, Package, AlertCircle, RefreshCw, MapPin } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';

type EmptyStateType = 'no-deals' | 'no-results' | 'error' | 'no-country' | 'loading-error';

interface Props {
  type: EmptyStateType;
  searchQuery?: string;
  onRetry?: () => void;
  onSelectCountry?: () => void;
}

const STATE_CONFIG: Record<
  EmptyStateType,
  {
    icon: React.ComponentType<{ className?: string }>;
    titleKey: string;
    titleFallback: string;
    descriptionKey: string;
    descriptionFallback: string;
    iconBgColor: string;
    iconColor: string;
  }
> = {
  'no-deals': {
    icon: Package,
    titleKey: 'emptyState.noDeals.title',
    titleFallback: 'No deals available',
    descriptionKey: 'emptyState.noDeals.description',
    descriptionFallback: 'There are no deals matching your filters right now. Try selecting a different category or store.',
    iconBgColor: 'bg-gray-100',
    iconColor: 'text-gray-400',
  },
  'no-results': {
    icon: Search,
    titleKey: 'emptyState.noResults.title',
    titleFallback: 'No results found',
    descriptionKey: 'emptyState.noResults.description',
    descriptionFallback: 'We couldn\'t find any deals matching "{query}". Try a different search term.',
    iconBgColor: 'bg-blue-100',
    iconColor: 'text-blue-500',
  },
  error: {
    icon: AlertCircle,
    titleKey: 'emptyState.error.title',
    titleFallback: 'Something went wrong',
    descriptionKey: 'emptyState.error.description',
    descriptionFallback: 'We had trouble loading deals. Please try again.',
    iconBgColor: 'bg-red-100',
    iconColor: 'text-red-500',
  },
  'no-country': {
    icon: MapPin,
    titleKey: 'emptyState.noCountry.title',
    titleFallback: 'Select a country',
    descriptionKey: 'emptyState.noCountry.description',
    descriptionFallback: 'Choose a country to see available deals in your area.',
    iconBgColor: 'bg-primary-100',
    iconColor: 'text-primary-600',
  },
  'loading-error': {
    icon: RefreshCw,
    titleKey: 'emptyState.loadingError.title',
    titleFallback: 'Failed to load',
    descriptionKey: 'emptyState.loadingError.description',
    descriptionFallback: 'Could not load deals. Check your connection and try again.',
    iconBgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
};

export default function EmptyState({ type, searchQuery, onRetry, onSelectCountry }: Props) {
  const { t } = useLanguage();
  const config = STATE_CONFIG[type];
  const Icon = config.icon;

  // Replace {query} placeholder in description
  let description = t(config.descriptionKey, config.descriptionFallback);
  if (searchQuery) {
    description = description.replace('{query}', searchQuery);
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      {/* Icon */}
      <div className={`w-20 h-20 rounded-full ${config.iconBgColor} flex items-center justify-center mb-6`}>
        <Icon className={`w-10 h-10 ${config.iconColor}`} />
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-gray-900 mb-2">
        {t(config.titleKey, config.titleFallback)}
      </h3>

      {/* Description */}
      <p className="text-gray-600 max-w-md mb-6">{description}</p>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {type === 'error' || type === 'loading-error' ? (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('emptyState.retry', 'Try Again')}
          </button>
        ) : type === 'no-country' ? (
          <button
            onClick={onSelectCountry}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            {t('emptyState.selectCountry', 'Select Country')}
          </button>
        ) : type === 'no-results' && searchQuery ? (
          <p className="text-sm text-gray-500">
            {t('emptyState.searchTip', 'Tip: Try searching for product names or categories like "KALLAX" or "sofa"')}
          </p>
        ) : null}
      </div>

      {/* Helpful Links */}
      {(type === 'no-deals' || type === 'no-results') && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            {t('emptyState.suggestions', 'You might also like:')}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <SuggestionChip label={t('emptyState.lastChance', 'Last Chance')} category="last_chance" />
            <SuggestionChip label={t('emptyState.familyOffers', 'Family Offers')} category="family_offers" />
            <SuggestionChip label={t('emptyState.asIs', 'As-Is')} category="as_is" />
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionChip({ label, category }: { label: string; category: string }) {
  return (
    <a
      href={`/deals?category=${category}`}
      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors"
    >
      {label}
    </a>
  );
}

/**
 * Inline loading error for partial failures
 */
export function InlineError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useLanguage();

  return (
    <div className="flex items-center justify-center gap-3 py-4 px-6 bg-red-50 border border-red-200 rounded-xl">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
      <p className="text-sm text-red-700">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t('emptyState.retry', 'Retry')}
        </button>
      )}
    </div>
  );
}
